import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api, API_BASE, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Loader2, Plus, CheckCircle2, XCircle, Fingerprint, ClipboardList, Upload, Image as ImageIcon, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

// Central styling primitives — matches the design used in other Absensi
// pages (AttendanceReport.jsx / Attendance.jsx) so this feature blends in
// without introducing a new visual language.
const inputCls =
  "w-full h-10 px-3 rounded-md border border-[#E5E7EB] bg-white focus:outline-none focus:ring-2 focus:ring-[#0055FF] focus:border-transparent text-sm";

const STATUS_META = {
  PENDING: { label: "Menunggu", bg: "bg-[#FEF3C7]", fg: "text-[#92400E]" },
  APPROVED: { label: "Disetujui", bg: "bg-[#D1FAE5]", fg: "text-[#065F46]" },
  REJECTED: { label: "Ditolak", bg: "bg-[#FEE2E2]", fg: "text-[#991B1B]" },
};

const TYPE_META = {
  IZIN:  { label: "Izin",  bg: "bg-[#DBEAFE]", fg: "text-[#1E3A8A]" },
  SAKIT: { label: "Sakit", bg: "bg-[#FFE4E6]", fg: "text-[#9F1239]" },
  CUTI:  { label: "Cuti",  bg: "bg-[#E9D5FF]", fg: "text-[#6B21A8]" },
};

function StatusBadge({ status }) {
  const m = STATUS_META[status] || { label: status, bg: "bg-[#F3F4F6]", fg: "text-[#4B5563]" };
  return <span data-testid={`status-${status}`} className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider ${m.bg} ${m.fg}`}>{m.label}</span>;
}

function TypeBadge({ type }) {
  const m = TYPE_META[type] || { label: type, bg: "bg-[#F3F4F6]", fg: "text-[#4B5563]" };
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider ${m.bg} ${m.fg}`}>{m.label}</span>;
}

export default function AttendanceRequests() {
  const { user } = useAuth();
  const role = (user?.role || "").toLowerCase();
  const canReview = role === "admin" || role === "koordinator";
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openCreate, setOpenCreate] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ type: "IZIN", start_date: "", end_date: "", reason: "", evidence_url: "" });
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterType, setFilterType] = useState("");
  // Sick-note upload state (only used while filling the SAKIT form).
  const [evidenceFile, setEvidenceFile] = useState(null);      // File object being previewed
  const [evidencePreviewSrc, setEvidencePreviewSrc] = useState(""); // local ObjectURL for the preview
  const [uploadingEvidence, setUploadingEvidence] = useState(false);
  // Modal state for the big preview shown when clicking a thumbnail in the table.
  const [previewSrc, setPreviewSrc] = useState("");

  // Feb 2026 — Deep-link `?open=<id>` dari notif approve/reject.
  // Setelah rows di-load, scroll ke row & flash background selama 2s.
  // URL dibersihkan supaya reload/refresh tidak re-flash.
  const location = useLocation();
  const navigate = useNavigate();
  const rowRefs = useRef({});
  const [highlightId, setHighlightId] = useState(null);
  useEffect(() => {
    const usp = new URLSearchParams(location.search);
    const openId = usp.get("open");
    if (!openId || loading) return;
    if (!rows.find((r) => r.id === openId)) return; // row belum ada di scope
    setHighlightId(openId);
    // Scroll ke elemen (bisa jadi ada 2 elemen — row (desktop) + card (mobile))
    requestAnimationFrame(() => {
      const el = rowRefs.current[openId];
      if (el && typeof el.scrollIntoView === "function") {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    });
    // Hapus param supaya reload tidak re-trigger highlight
    usp.delete("open");
    const qs = usp.toString();
    navigate(`/attendance-requests${qs ? `?${qs}` : ""}`, { replace: true });
    const timer = setTimeout(() => setHighlightId(null), 2400);
    return () => clearTimeout(timer);
  }, [location.search, rows, loading, navigate]);

  // Small helper — builds the auth-gated proxy URL for a stored sick-note.
  // Legacy `evidence_url` values that were entered as raw http(s) links are
  // returned as-is so existing data keeps working.
  const evidenceHref = (row) => {
    const ev = row?.evidence_url;
    if (!ev) return "";
    if (ev.startsWith("http://") || ev.startsWith("https://")) return ev;
    const token = localStorage.getItem("aroma_token") || "";
    return `${API_BASE}/attendance/requests/${row.id}/evidence?auth=${encodeURIComponent(token)}`;
  };
  const evidenceIsExternal = (row) => {
    const ev = row?.evidence_url || "";
    return ev.startsWith("http://") || ev.startsWith("https://");
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterStatus) params.status = filterStatus;
      if (filterType) params.type = filterType;
      const { data } = await api.get("/attendance/requests", { params });
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Gagal memuat pengajuan");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterStatus, filterType]);

  const create = async (e) => {
    e.preventDefault();
    if (!form.reason || form.reason.trim().length < 3) {
      toast.error("Alasan wajib diisi minimal 3 karakter");
      return;
    }
    setSaving(true);
    try {
      await api.post("/attendance/requests", { ...form, evidence_url: form.evidence_url || undefined });
      toast.success("Pengajuan dikirim");
      setOpenCreate(false);
      setForm({ type: "IZIN", start_date: "", end_date: "", reason: "", evidence_url: "" });
      // Release any local ObjectURL to avoid leaks.
      if (evidencePreviewSrc) URL.revokeObjectURL(evidencePreviewSrc);
      setEvidenceFile(null);
      setEvidencePreviewSrc("");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Gagal mengirim pengajuan");
    } finally {
      setSaving(false);
    }
  };

  // Uploads the chosen sick-note image to the backend and stores the
  // returned storage `path` into form.evidence_url. Any error resets the
  // preview so the user knows to retry.
  const uploadEvidence = async (file) => {
    if (!file) return;
    if (!file.type || !file.type.startsWith("image/")) {
      toast.error("File harus berupa gambar");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Ukuran maksimum 8 MB");
      return;
    }
    setUploadingEvidence(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/attendance/requests/evidence", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const path = data?.path || "";
      if (!path) throw new Error("Path tidak diterima");
      // Swap the local preview URL for the actual file so the modal in
      // the create dialog keeps showing the same picture.
      if (evidencePreviewSrc) URL.revokeObjectURL(evidencePreviewSrc);
      const localUrl = URL.createObjectURL(file);
      setEvidencePreviewSrc(localUrl);
      setEvidenceFile(file);
      setForm((f) => ({ ...f, evidence_url: path }));
      toast.success("Bukti terunggah");
    } catch (e) {
      // Roll back preview so state matches "no evidence uploaded".
      if (evidencePreviewSrc) URL.revokeObjectURL(evidencePreviewSrc);
      setEvidencePreviewSrc("");
      setEvidenceFile(null);
      setForm((f) => ({ ...f, evidence_url: "" }));
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Gagal mengunggah bukti");
    } finally {
      setUploadingEvidence(false);
    }
  };

  const clearEvidence = () => {
    if (evidencePreviewSrc) URL.revokeObjectURL(evidencePreviewSrc);
    setEvidencePreviewSrc("");
    setEvidenceFile(null);
    setForm((f) => ({ ...f, evidence_url: "" }));
  };

  const approve = async (r) => {
    try {
      const { data } = await api.post(`/attendance/requests/${r.id}/approve`);
      toast.success(`Disetujui — ${data.materialised_days} hari absensi ditandai ${r.type}`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Gagal menyetujui");
    }
  };

  const submitReject = async () => {
    if (!rejectTarget) return;
    try {
      await api.post(`/attendance/requests/${rejectTarget.id}/reject`, { rejection_reason: rejectReason.trim() || undefined });
      toast.success("Pengajuan ditolak");
      setRejectTarget(null);
      setRejectReason("");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Gagal menolak");
    }
  };

  const pendingCount = useMemo(() => rows.filter((r) => r.status === "PENDING").length, [rows]);

  return (
    <div className="max-w-6xl mx-auto space-y-6 px-2 md:px-0" data-testid="attendance-requests-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.25em] text-[#9CA3AF] flex items-center gap-1.5">
            <Fingerprint className="h-3 w-3" /> Absensi
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tighter mt-1">Pengajuan Absensi</h1>
          <p className="text-sm text-[#4B5563] mt-2">
            {canReview
              ? `Approve / reject pengajuan izin, sakit, dan cuti dari teknisi. ${pendingCount} menunggu review.`
              : "Ajukan izin, sakit, atau cuti. Pengajuan akan direview koordinator wilayah Anda."}
          </p>
        </div>
        <button
          onClick={() => setOpenCreate(true)}
          data-testid="btn-new-request"
          className="h-10 px-4 rounded-md bg-[#0055FF] hover:bg-[#0044CC] text-white text-sm font-semibold flex items-center gap-2"
        >
          <Plus className="h-4 w-4" /> Ajukan Baru
        </button>
      </div>

      {/* Filters — full-width vertical stack on mobile, horizontal on ≥sm */}
      <div className="bg-white border border-[#E5E7EB] rounded-md p-3 flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-3">
        <div className="text-[10px] font-bold uppercase tracking-widest text-[#9CA3AF]">Filter</div>
        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className={inputCls + " h-10 w-full sm:h-8 sm:w-40"} data-testid="filter-status">
          <option value="">Semua Status</option>
          <option value="PENDING">Menunggu</option>
          <option value="APPROVED">Disetujui</option>
          <option value="REJECTED">Ditolak</option>
        </select>
        <select value={filterType} onChange={(e) => setFilterType(e.target.value)} className={inputCls + " h-10 w-full sm:h-8 sm:w-40"} data-testid="filter-type">
          <option value="">Semua Jenis</option>
          <option value="IZIN">Izin</option>
          <option value="SAKIT">Sakit</option>
          <option value="CUTI">Cuti</option>
        </select>
      </div>

      {/* Desktop table — hidden on mobile to prevent horizontal scroll.
          Data source & business logic identical; only presentation differs. */}
      <div className="hidden md:block bg-white border border-[#E5E7EB] rounded-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-[10px] font-bold uppercase tracking-widest text-[#9CA3AF] bg-[#F8F9FA]">
              <tr>
                {canReview && <th className="px-4 py-3">Teknisi</th>}
                <th className="px-4 py-3">Jenis</th>
                <th className="px-4 py-3">Periode</th>
                <th className="px-4 py-3">Alasan</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Reviewer</th>
                <th className="px-4 py-3 text-right">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={canReview ? 7 : 6} className="px-4 py-8 text-center text-[#9CA3AF]"><Loader2 className="h-4 w-4 animate-spin inline mr-2" />Memuat…</td></tr>
              )}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={canReview ? 7 : 6} className="px-4 py-8 text-center text-[#9CA3AF]">
                  <ClipboardList className="h-6 w-6 mx-auto mb-2" />
                  Belum ada pengajuan.
                </td></tr>
              )}
              {!loading && rows.map((r) => (
                <tr
                  key={r.id}
                  data-testid={`row-${r.id}`}
                  ref={(el) => { if (el) rowRefs.current[r.id] = el; }}
                  className={`border-t border-[#E5E7EB] hover:bg-[#F8F9FA] transition-colors ${
                    highlightId === r.id ? "flash-highlight" : ""
                  }`}
                >
                  {canReview && (
                    <td className="px-4 py-3">
                      <div className="font-semibold">{r.user_name || r.username}</div>
                      <div className="text-[10px] text-[#9CA3AF] uppercase">{r.user_role}</div>
                    </td>
                  )}
                  <td className="px-4 py-3"><TypeBadge type={r.type} /></td>
                  <td className="px-4 py-3 font-mono-tab text-xs">
                    {r.start_date}
                    {r.end_date !== r.start_date && <> — {r.end_date}</>}
                  </td>
                  <td className="px-4 py-3 max-w-xs">
                    <div className="text-xs truncate" title={r.reason}>{r.reason}</div>
                    {r.evidence_url && (
                      evidenceIsExternal(r) ? (
                        <a
                          href={evidenceHref(r)}
                          target="_blank"
                          rel="noreferrer"
                          data-testid={`evidence-link-${r.id}`}
                          className="text-[10px] text-[#0055FF] hover:underline"
                        >
                          Bukti (tautan)
                        </a>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setPreviewSrc(evidenceHref(r))}
                          data-testid={`evidence-thumb-${r.id}`}
                          className="mt-1 inline-block group relative"
                          title="Lihat bukti sakit"
                        >
                          <img
                            src={evidenceHref(r)}
                            alt="Bukti sakit"
                            loading="lazy"
                            className="h-10 w-10 rounded border border-[#E5E7EB] object-cover group-hover:border-[#0055FF]"
                          />
                          <span className="absolute inset-0 rounded bg-black/0 group-hover:bg-black/10 flex items-center justify-center transition">
                            <ImageIcon className="h-3.5 w-3.5 text-white opacity-0 group-hover:opacity-100" />
                          </span>
                        </button>
                      )
                    )}
                    {r.rejection_reason && <div className="text-[10px] text-[#991B1B] mt-0.5">Tolak: {r.rejection_reason}</div>}
                  </td>
                  <td className="px-4 py-3"><StatusBadge status={r.status} /></td>
                  <td className="px-4 py-3 text-xs">
                    {r.reviewed_by || <span className="text-[#9CA3AF]">—</span>}
                    {r.reviewed_at && <div className="text-[10px] text-[#9CA3AF] font-mono-tab">{(r.reviewed_at || "").slice(0, 10)}</div>}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {canReview && r.status === "PENDING" ? (
                      <div className="inline-flex gap-1">
                        <button onClick={() => approve(r)} data-testid={`btn-approve-${r.id}`} className="h-8 px-3 rounded-md bg-[#065F46] hover:bg-[#064E3B] text-white text-xs font-bold flex items-center gap-1">
                          <CheckCircle2 className="h-3 w-3" /> Setujui
                        </button>
                        <button onClick={() => { setRejectTarget(r); setRejectReason(""); }} data-testid={`btn-reject-${r.id}`} className="h-8 px-3 rounded-md border border-[#991B1B] text-[#991B1B] hover:bg-[#FEE2E2] text-xs font-bold flex items-center gap-1">
                          <XCircle className="h-3 w-3" /> Tolak
                        </button>
                      </div>
                    ) : (
                      <span className="text-[10px] text-[#9CA3AF]">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Mobile cards — one per pengajuan. Uses the same `rows` state as
          the desktop table so business logic, security scope and API
          calls are 100% identical. Approve/reject buttons appear only
          when the caller `canReview` and status === PENDING (same rule
          as desktop). */}
      <div className="md:hidden space-y-3" data-testid="mobile-requests-list">
        {loading && (
          <div className="bg-white border border-[#E5E7EB] rounded-md p-8 text-center text-[#9CA3AF]">
            <Loader2 className="h-4 w-4 animate-spin inline mr-2" />Memuat…
          </div>
        )}
        {!loading && rows.length === 0 && (
          <div className="bg-white border border-[#E5E7EB] rounded-md p-8 text-center text-[#9CA3AF]">
            <ClipboardList className="h-6 w-6 mx-auto mb-2" />
            Belum ada pengajuan.
          </div>
        )}
        {!loading && rows.map((r) => (
          <div
            key={r.id}
            data-testid={`card-${r.id}`}
            ref={(el) => { if (el) rowRefs.current[r.id] = el; }}
            className={`bg-white border border-[#E5E7EB] rounded-md p-4 space-y-3 transition-colors ${
              highlightId === r.id ? "flash-highlight" : ""
            }`}
          >
            {/* Header: nama + role + jenis + status */}
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                {canReview && (
                  <>
                    <div className="font-semibold text-sm truncate">{r.user_name || r.username}</div>
                    <div className="text-[10px] text-[#9CA3AF] uppercase tracking-widest">{r.user_role}</div>
                  </>
                )}
                {!canReview && (
                  <div className="text-[10px] text-[#9CA3AF] uppercase tracking-widest">Pengajuan Saya</div>
                )}
              </div>
              <div className="flex flex-col items-end gap-1 shrink-0">
                <TypeBadge type={r.type} />
                <StatusBadge status={r.status} />
              </div>
            </div>

            <div className="border-t border-[#E5E7EB] pt-3 space-y-2.5 text-xs">
              <div>
                <div className="text-[10px] font-bold uppercase tracking-widest text-[#9CA3AF] mb-0.5">Periode</div>
                <div className="font-mono-tab">
                  {r.start_date}
                  {r.end_date !== r.start_date && <> — {r.end_date}</>}
                </div>
              </div>

              <div>
                <div className="text-[10px] font-bold uppercase tracking-widest text-[#9CA3AF] mb-0.5">Alasan</div>
                <div className="whitespace-pre-wrap break-words leading-relaxed">{r.reason || <span className="text-[#9CA3AF]">—</span>}</div>
                {r.rejection_reason && (
                  <div className="mt-1 text-[11px] text-[#991B1B]">Alasan tolak: {r.rejection_reason}</div>
                )}
              </div>

              {r.evidence_url && (
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-widest text-[#9CA3AF] mb-1">Bukti Sakit</div>
                  {evidenceIsExternal(r) ? (
                    <a
                      href={evidenceHref(r)}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`m-evidence-link-${r.id}`}
                      className="text-[11px] text-[#0055FF] hover:underline"
                    >
                      Buka tautan bukti
                    </a>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setPreviewSrc(evidenceHref(r))}
                      data-testid={`m-evidence-thumb-${r.id}`}
                      className="inline-block group relative"
                      title="Lihat bukti sakit"
                    >
                      <img
                        src={evidenceHref(r)}
                        alt="Bukti sakit"
                        loading="lazy"
                        className="h-16 w-16 rounded border border-[#E5E7EB] object-cover"
                      />
                    </button>
                  )}
                </div>
              )}

              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-[10px] font-bold uppercase tracking-widest text-[#9CA3AF] mb-0.5">Diajukan</div>
                  <div className="font-mono-tab text-[11px] text-[#4B5563]">{(r.created_at || "").slice(0, 16).replace("T", " ")}</div>
                </div>
                {r.reviewed_by && (
                  <div className="text-right min-w-0">
                    <div className="text-[10px] font-bold uppercase tracking-widest text-[#9CA3AF] mb-0.5">Reviewer</div>
                    <div className="text-[11px] truncate">{r.reviewed_by}</div>
                    {r.reviewed_at && <div className="text-[10px] text-[#9CA3AF] font-mono-tab">{(r.reviewed_at || "").slice(0, 10)}</div>}
                  </div>
                )}
              </div>
            </div>

            {/* Aksi: hanya untuk role yang berhak & status PENDING —
                touch-friendly h-11 dengan lebar penuh. */}
            {canReview && r.status === "PENDING" && (
              <div className="border-t border-[#E5E7EB] pt-3 grid grid-cols-2 gap-2">
                <button
                  onClick={() => { setRejectTarget(r); setRejectReason(""); }}
                  data-testid={`m-btn-reject-${r.id}`}
                  className="h-11 rounded-md border border-[#991B1B] text-[#991B1B] hover:bg-[#FEE2E2] text-sm font-bold flex items-center justify-center gap-1.5"
                >
                  <XCircle className="h-4 w-4" /> Tolak
                </button>
                <button
                  onClick={() => approve(r)}
                  data-testid={`m-btn-approve-${r.id}`}
                  className="h-11 rounded-md bg-[#065F46] hover:bg-[#064E3B] text-white text-sm font-bold flex items-center justify-center gap-1.5"
                >
                  <CheckCircle2 className="h-4 w-4" /> Setujui
                </button>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Create dialog */}
      <Dialog open={openCreate} onOpenChange={setOpenCreate}>
        <DialogContent data-testid="dialog-create-request">
          <DialogHeader>
            <DialogTitle className="font-display text-xl font-bold tracking-tight">Ajukan Ketidakhadiran</DialogTitle>
          </DialogHeader>
          <form onSubmit={create} className="space-y-3">
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-widest text-[#4B5563] mb-1.5">Jenis</label>
              <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className={inputCls} data-testid="req-type">
                <option value="IZIN">Izin</option>
                <option value="SAKIT">Sakit</option>
                <option value="CUTI">Cuti</option>
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-widest text-[#4B5563] mb-1.5">Mulai</label>
                <input type="date" required value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className={inputCls} data-testid="req-start" />
              </div>
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-widest text-[#4B5563] mb-1.5">Selesai</label>
                <input type="date" required value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className={inputCls} data-testid="req-end" />
              </div>
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-widest text-[#4B5563] mb-1.5">Alasan (wajib)</label>
              <textarea rows={3} required minLength={3} maxLength={500} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} className={inputCls + " h-auto py-2"} data-testid="req-reason" placeholder="Jelaskan alasan…" />
            </div>
            {form.type === "SAKIT" && (
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-widest text-[#4B5563] mb-1.5">Bukti Sakit (opsional — foto surat dokter)</label>
                {!evidencePreviewSrc && !form.evidence_url && (
                  <label
                    className={`flex items-center justify-center gap-2 h-24 rounded-md border-2 border-dashed border-[#E5E7EB] cursor-pointer hover:border-[#0055FF] hover:bg-[#F8F9FA] transition ${uploadingEvidence ? "opacity-60 pointer-events-none" : ""}`}
                    data-testid="req-evidence-picker"
                  >
                    {uploadingEvidence ? (
                      <><Loader2 className="h-4 w-4 animate-spin text-[#0055FF]" /><span className="text-xs text-[#4B5563]">Mengunggah…</span></>
                    ) : (
                      <><Upload className="h-4 w-4 text-[#0055FF]" /><span className="text-xs text-[#4B5563]">Pilih foto / ambil dari kamera (JPG/PNG, ≤ 8 MB)</span></>
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      capture="environment"
                      className="hidden"
                      data-testid="req-evidence-file"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) uploadEvidence(f);
                        e.target.value = "";
                      }}
                    />
                  </label>
                )}
                {(evidencePreviewSrc || form.evidence_url) && (
                  <div className="flex items-center gap-3 p-2 border border-[#E5E7EB] rounded-md">
                    <img
                      src={evidencePreviewSrc || ""}
                      alt="Preview bukti"
                      className="h-16 w-16 rounded object-cover border border-[#E5E7EB]"
                      data-testid="req-evidence-preview"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-semibold text-[#065F46] flex items-center gap-1">
                        <CheckCircle2 className="h-3 w-3" /> Bukti terunggah
                      </div>
                      <div className="text-[10px] text-[#9CA3AF] truncate">{evidenceFile?.name || form.evidence_url}</div>
                    </div>
                    <button
                      type="button"
                      onClick={clearEvidence}
                      data-testid="req-evidence-clear"
                      className="h-8 px-2 rounded-md border border-[#E5E7EB] text-[#991B1B] hover:bg-[#FEE2E2] text-xs font-bold flex items-center gap-1"
                    >
                      <X className="h-3 w-3" /> Ganti
                    </button>
                  </div>
                )}
              </div>
            )}
            <DialogFooter>
              <button type="submit" disabled={saving} className="h-10 px-5 rounded-md bg-[#0055FF] hover:bg-[#0044CC] text-white font-semibold flex items-center gap-2" data-testid="btn-submit-request">
                {saving && <Loader2 className="h-4 w-4 animate-spin" />} Kirim
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Reject dialog */}
      <Dialog open={!!rejectTarget} onOpenChange={(v) => !v && setRejectTarget(null)}>
        <DialogContent data-testid="dialog-reject">
          <DialogHeader>
            <DialogTitle className="font-display text-xl font-bold tracking-tight">Tolak Pengajuan</DialogTitle>
          </DialogHeader>
          {rejectTarget && (
            <div className="space-y-3">
              <div className="bg-[#F8F9FA] rounded-md p-3 text-xs">
                <div><b>{rejectTarget.user_name || rejectTarget.username}</b> · {rejectTarget.type} {rejectTarget.start_date}{rejectTarget.end_date !== rejectTarget.start_date && ` — ${rejectTarget.end_date}`}</div>
                <div className="mt-1 text-[#4B5563]">"{rejectTarget.reason}"</div>
              </div>
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-widest text-[#4B5563] mb-1.5">Alasan Penolakan (opsional)</label>
                <textarea rows={3} maxLength={500} value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} className={inputCls + " h-auto py-2"} data-testid="reject-reason" />
              </div>
            </div>
          )}
          <DialogFooter>
            <button onClick={submitReject} className="h-10 px-5 rounded-md bg-[#991B1B] hover:bg-[#7F1D1D] text-white font-semibold" data-testid="btn-confirm-reject">Tolak</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Big preview modal for sick-note thumbnails */}
      <Dialog open={!!previewSrc} onOpenChange={(v) => !v && setPreviewSrc("")}>
        <DialogContent data-testid="dialog-evidence-preview" className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-display text-lg font-bold tracking-tight">Bukti Sakit</DialogTitle>
          </DialogHeader>
          {previewSrc && (
            <div className="bg-[#0B0F19] rounded-md flex items-center justify-center p-2 max-h-[70vh] overflow-auto">
              <img src={previewSrc} alt="Bukti sakit" className="max-w-full max-h-[65vh] object-contain" data-testid="evidence-preview-img" />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
